import {
  SingleProductData,
  VariantsSingleProduct,
  MediaItem,
} from "@repo/apis/single-product";
import { StockPriceData, VariantsStockPrice } from "@repo/apis/stockprice";
import { updateSizeAndFitString } from "../utils/update-size-and-fit-string";
import { generateVideoUrls } from "./generate-video-urls";

type CatalogueProps = {
  productData: SingleProductData;
};

export type CatalogueMappedProduct = SingleProductData & {
  media: {
    images: MediaItem[];
    videoUrls: {
      mobile: string;
      desktop: string;
    };
  };
};

export function mapCatalogueData({
  productData,
}: CatalogueProps): CatalogueMappedProduct {
  const mappedImages = productData.media.images.map((image) => ({
    ...image,
    url: image.url,
  }));

  const extractAssetIdFromUrl = (url: string | undefined) => {
    if (!url) return null;

    const match = url.match(/\/(\d+)-catwalk(?:-\w+)?$/);
    const extractedId = match ? match[1] : null;

    return extractedId ? `${extractedId}-catwalk` : null;
  };

  const videoAssetId = productData.media.catwalk[0]?.assetId
    ? `${productData.media.catwalk[0].assetId}`
    : extractAssetIdFromUrl(productData.media.catwalk[0]?.url);

  const videoUrls = generateVideoUrls(videoAssetId);

  return {
    ...productData,
    media: {
      ...productData.media,
      images: mappedImages,
      videoUrls,
    },
    info: {
      ...productData.info,
      sizeAndFit: updateSizeAndFitString(productData.info.sizeAndFit),
    },
  };
}

export type MergedVariant = VariantsSingleProduct & VariantsStockPrice;

export type StockPriceMappedProduct = Omit<CatalogueMappedProduct, "variants"> &
  Omit<StockPriceData, "variants"> & {
    variants: MergedVariant[];
    media: {
      images: MediaItem[];
      videoUrls: {
        mobile: string;
        desktop: string;
      };
    };
  };

type StockPriceProductProps = {
  productData: CatalogueMappedProduct;
  stockPriceData: StockPriceData;
};

export function mapProductStockPriceData({
  productData,
  stockPriceData,
}: StockPriceProductProps): StockPriceMappedProduct {
  const { variants: stockPriceVariants, ...restOfStockPriceData } =
    stockPriceData;

  const mappedVariants = productData.variants.flatMap((variant) => {
    const stockVariant = stockPriceVariants.find(
      (stockPriceVariant) => stockPriceVariant.id === variant.id,
    );

    if (!stockVariant) {
      console.error(`Stock variant not found for variant ID: ${variant.id}`);
      return [];
    }

    return [
      {
        ...stockVariant,
        ...variant,
      },
    ];
  });

  return {
    ...productData,
    ...restOfStockPriceData,
    variants: mappedVariants,
  };
}
